import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Wallet, 
  Loader2,
  ExternalLink,
  Clock,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Copy
} from 'lucide-react';

export default function Withdraw() {
  const { user, token, api, refreshUser } = useAuth();
  const [amount, setAmount] = useState('');
  const [gamepassId, setGamepassId] = useState('');
  const [loading, setLoading] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState({ deposit_count: 0, withdraw_count: 0 });
  const [currentOrder, setCurrentOrder] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token) {
      fetchMyOrders();
      fetchPendingOrders();
    }
  }, [token]);

  // Poll for order updates
  useEffect(() => {
    if (currentOrder && currentOrder.status === 'pending') {
      const interval = setInterval(async () => {
        try {
          const res = await api.get(`/robux/order/${currentOrder.order_id}`);
          if (res.data.status !== currentOrder.status) {
            setCurrentOrder(res.data);
            fetchMyOrders();
            if (res.data.status === 'completed') {
              toast.success('Withdrawal completed! Your gamepass was purchased. Check your Roblox account for Robux!');
              refreshUser();
            }
          }
        } catch (e) {}
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [currentOrder]);

  const fetchMyOrders = async () => {
    try {
      const res = await api.get('/robux/my-orders');
      setMyOrders(res.data.orders || []);
      
      // Check for active withdraw order
      const activeWithdraw = res.data.orders?.find(o => 
        o.type === 'withdraw' && ['pending', 'processing'].includes(o.status)
      );
      if (activeWithdraw) {
        setCurrentOrder(activeWithdraw);
      }
    } catch (e) {}
  };

  const fetchPendingOrders = async () => {
    try {
      const res = await api.get('/robux/pending-orders');
      setPendingOrders(res.data);
    } catch (e) {}
  };

  const handleWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);
    if (!withdrawAmount || withdrawAmount < 10) {
      toast.error('Minimum withdrawal is R$10');
      return;
    }
    if (!gamepassId.trim()) {
      toast.error('Please enter your gamepass ID or URL');
      return;
    }
    if (withdrawAmount > (user?.site_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/robux/withdraw', { 
        amount: withdrawAmount,
        gamepass_id: gamepassId.trim()
      });
      
      if (res.data.status === 'completed') {
        toast.success(res.data.message);
        setCurrentOrder(null);
        refreshUser();
      } else {
        toast.success(res.data.message);
        setCurrentOrder(res.data);
      }
      
      setAmount('');
      setGamepassId('');
      fetchMyOrders();
      fetchPendingOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create withdrawal order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!currentOrder) return;
    
    try {
      await api.post(`/robux/cancel/${currentOrder.order_id}`);
      toast.success('Order cancelled and balance refunded');
      setCurrentOrder(null);
      fetchMyOrders();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const copyGamepassUrl = () => {
    if (currentOrder?.gamepass_url) {
      navigator.clipboard.writeText(currentOrder.gamepass_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const quickAmounts = [100, 500, 1000, 5000];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Wallet className="text-orange-400" />
          Withdraw Robux
        </h1>
        <p className="text-slate-400 mb-2">Withdraw your balance to Robux</p>
        
        {user && (
          <p className="text-slate-300 mb-6">
            Available: <span className="text-green-400 font-bold">R${(user.site_balance || 0).toLocaleString()}</span>
          </p>
        )}

        {/* Current Active Order */}
        {currentOrder && (
          <div className="bg-slate-800/80 rounded-xl p-6 mb-6 border border-slate-700">
            
            {/* STATUS: PENDING - Waiting for depositor to buy */}
            {currentOrder.status === 'pending' && (
              <>
                <div className="flex items-center gap-2 text-yellow-400 mb-4">
                  <Clock className="animate-pulse" />
                  <span className="font-semibold">Waiting for Auto-Purchase...</span>
                </div>
                <p className="text-slate-300 mb-4">
                  Your withdrawal of <span className="text-orange-400 font-bold">R${currentOrder.amount?.toLocaleString()}</span> is waiting to be matched with a depositor.
                </p>
                
                <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                  <p className="text-slate-400 text-sm mb-2">Your Gamepass:</p>
                  <div className="flex items-center gap-2">
                    <a 
                      href={currentOrder.gamepass_url || `https://www.roblox.com/game-pass/${currentOrder.gamepass_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline text-sm flex-1 truncate"
                    >
                      {currentOrder.gamepass_url || `Game Pass ${currentOrder.gamepass_id}`}
                    </a>
                    <button
                      onClick={copyGamepassUrl}
                      className="p-2 hover:bg-slate-700 rounded transition"
                    >
                      {copied ? <CheckCircle className="text-green-400" size={16} /> : <Copy className="text-slate-400" size={16} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
                  <RefreshCw size={14} className="animate-spin" />
                  When a depositor matches, your gamepass will be auto-purchased...
                </div>
                <Button variant="outline" onClick={handleCancel} className="text-red-400 border-red-400/50">
                  Cancel & Refund
                </Button>
              </>
            )}

            {/* STATUS: COMPLETED */}
            {currentOrder.status === 'completed' && (
              <>
                <div className="flex items-center gap-2 text-green-400 mb-4">
                  <CheckCircle />
                  <span className="font-semibold">Completed!</span>
                </div>
                <p className="text-slate-300">
                  Your withdrawal is complete! Your gamepass was purchased. Check your Roblox account for Robux!
                </p>
                <Button 
                  onClick={() => setCurrentOrder(null)} 
                  className="mt-4 bg-slate-700"
                >
                  Start New Withdrawal
                </Button>
              </>
            )}
          </div>
        )}

        {/* Withdraw Form - Only show when no active order */}
        {!currentOrder && (
          <div className="bg-slate-800/80 rounded-xl p-6 mb-6 border border-slate-700">
            {/* Roblox Fee Warning */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-orange-400 mt-0.5" size={18} />
                <div>
                  <p className="text-orange-400 font-medium">Roblox Marketplace Fee</p>
                  <p className="text-slate-300 text-sm">
                    Roblox takes a 30% fee when your gamepass is purchased. You'll receive 70% of your withdrawal amount.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-white mb-4">Step 1: Create Your Gamepass</h2>
            
            <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
              <p className="text-slate-300 text-sm mb-3">
                First, create a gamepass in any of your Roblox games with your desired withdrawal amount as the price.
              </p>
              <a 
                href="https://create.roblox.com/dashboard/creations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition"
              >
                <ExternalLink size={16} />
                Open Roblox Creator Dashboard
              </a>
            </div>

            <h2 className="text-xl font-semibold text-white mb-4">Step 2: Enter Details</h2>
            
            {/* Amount Input */}
            <div className="mb-4">
              <label className="text-slate-300 text-sm mb-2 block">Withdrawal Amount (must match gamepass price)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-400 font-bold">R$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  max={user?.site_balance || 0}
                  className="w-full bg-slate-900/50 border border-slate-600 rounded-lg pl-12 pr-4 py-4 text-2xl text-white focus:outline-none focus:border-orange-500"
                />
              </div>
              {amount && parseFloat(amount) >= 10 && (
                <p className="text-slate-400 text-sm mt-2">
                  You will receive: <span className="text-green-400 font-bold">R${Math.floor(parseFloat(amount) * 0.7).toLocaleString()}</span> (after 30% Roblox fee)
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {quickAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setAmount(amt.toString())}
                  disabled={amt > (user?.site_balance || 0)}
                  className={`px-4 py-2 rounded-lg transition ${
                    amt > (user?.site_balance || 0)
                      ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                      : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-300'
                  }`}
                >
                  R${amt.toLocaleString()}
                </button>
              ))}
              <button
                onClick={() => setAmount((user?.site_balance || 0).toString())}
                className="px-4 py-2 bg-orange-600/30 hover:bg-orange-600/50 text-orange-300 rounded-lg transition"
              >
                Max
              </button>
            </div>

            {/* Gamepass ID Input */}
            <div className="mb-6">
              <label className="text-slate-300 text-sm mb-2 block">Gamepass ID or URL</label>
              <input
                type="text"
                value={gamepassId}
                onChange={(e) => setGamepassId(e.target.value)}
                placeholder="e.g., 12345678 or https://www.roblox.com/game-pass/12345678"
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Pending deposits info */}
            {pendingOrders.deposit_count > 0 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                <p className="text-green-400 text-sm">
                  {pendingOrders.deposit_count} deposit order(s) waiting - high chance of instant match!
                </p>
              </div>
            )}

            <Button 
              onClick={handleWithdraw} 
              disabled={loading || !amount || !gamepassId.trim() || parseFloat(amount) < 10 || parseFloat(amount) > (user?.site_balance || 0)}
              className="w-full bg-orange-600 hover:bg-orange-500 py-6 text-lg"
            >
              {loading ? <Loader2 className="animate-spin mr-2" /> : <ArrowRight className="mr-2" />}
              Withdraw R${amount ? parseFloat(amount).toLocaleString() : '0'}
            </Button>

            <p className="text-slate-500 text-sm mt-4 text-center">
              Your gamepass will be automatically purchased when matched with a depositor.
            </p>
          </div>
        )}

        {/* How it works */}
        <div className="bg-slate-800/50 rounded-xl p-6 mb-6 border border-slate-700/50">
          <h3 className="text-lg font-semibold text-white mb-3">How it Works</h3>
          <ol className="space-y-2 text-slate-400 text-sm">
            <li className="flex gap-2">
              <span className="text-orange-400 font-bold">1.</span>
              Create a gamepass in your Roblox game with the exact withdrawal price
            </li>
            <li className="flex gap-2">
              <span className="text-orange-400 font-bold">2.</span>
              Enter the amount and gamepass ID here
            </li>
            <li className="flex gap-2">
              <span className="text-orange-400 font-bold">3.</span>
              System matches you with a depositor automatically
            </li>
            <li className="flex gap-2">
              <span className="text-orange-400 font-bold">4.</span>
              Depositor's account auto-purchases your gamepass
            </li>
            <li className="flex gap-2">
              <span className="text-orange-400 font-bold">5.</span>
              You receive Robux in your Roblox account (minus 30% Roblox fee)
            </li>
          </ol>
        </div>

        {/* Order History */}
        {myOrders.length > 0 && (
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Withdrawals</h3>
            <div className="space-y-3">
              {myOrders.filter(o => o.type === 'withdraw').slice(0, 10).map(order => (
                <div key={order.order_id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                  <div>
                    <p className="text-white font-medium">R${order.amount?.toLocaleString()}</p>
                    <p className="text-slate-500 text-xs">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                    order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {order.status === 'completed' && order.auto_purchased ? 'Auto-Completed' : order.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
